import React, { useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Clock, User, Calendar, Tag, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";

const Blogs = () => {
  const [searchQuery, setSearchQuery] = useState("");

  // Sample blog posts data
  const blogPosts = [
    {
      id: "1",
      title: "Building Trust in Agentic AI: How MSSPs and Security Teams Can Confidently Rely on Autonomous AI in SOC Operations",
      excerpt: "Discover how security professionals can trust AI to execute important SOC tasks autonomously without sacrificing accuracy, explainability, and control.",
      author: "Spharaka Security Team",
      date: "August 15, 2025",
      readTime: "12 min read",
      tags: ["Agentic AI", "SOC Operations", "Trust & Security"],
      image: "/images/ai-robot-hand-human.jpg",
      slug: "building-trust-agentic-ai"
    },
    {
      id: "2",
      title: "How Agentic AI is Empowering MSSPs to Scale and Innovate with Confidence",
      excerpt: "Learn how Agentic AI enables MSSPs to scale faster, operate more efficiently, and deliver differentiated value while maintaining control and governance.",
      author: "Spharaka Security Team",
      date: "September 8, 2025",
      readTime: "10 min read",
      tags: ["MSSP", "Agentic AI", "Scaling"],
      image: "/images/mssp-agentic-ai.jpg",
      slug: "mssp-agentic-ai"
    },
    {
      id: "3",
      title: "How to Select the Right AI-Driven XDR Solution? A Guide for MSSPs and Security Professionals",
      excerpt: "This guide outlines crucial factors to consider, helping MSSPs confidently select an XDR solution that scales, integrates, and delivers true value.",
      author: "Spharaka Security Team",
      date: "August 28, 2025",
      readTime: "15 min read",
      tags: ["XDR Solutions", "MSSP Guide", "AI Security"],
      image: "/images/ai-xdr-solution-guide.png",
      slug: "ai-xdr-solution-guide"
    },
    {
      id: "4",
      title: "Understanding the Difference Between EDR, NDR, MDR, and XDR: What They Are and the Value They Deliver",
      excerpt: "This blog clarifies each concept and highlights the unique value each delivers, empowering the cybersecurity community to make informed decisions and optimize their security posture.",
      author: "Spharaka Security Team",
      date: "September 12, 2025",
      readTime: "12 min read",
      tags: ["EDR vs NDR vs MDR vs XDR", "Security Solutions", "Cybersecurity Guide"],
      image: "/images/edr-ndr-mdr-xdr-difference.png",
      slug: "edr-ndr-mdr-xdr-difference"
    },
    {
      id: "5",
      title: "From Reactive to Proactive: How AI Native SOCs Are Redefining Cybersecurity",
      excerpt: "Explore how AI Native SOCs powered by context engineering and agentic AI are transforming cybersecurity from reactive firefighting to proactive threat defense.",
      author: "Spharaka Security Team",
      date: "August 22, 2025",
      readTime: "12 min read",
      tags: ["AI Native SOC", "Context Engineering", "Proactive Security"],
      image: "/images/ai-native-soc-blog.png",
      slug: "reactive-to-proactive-soc"
    }
  ];

  // Filter blog posts based on search query
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return blogPosts;
    
    const query = searchQuery.toLowerCase();
    return blogPosts.filter(post => 
      post.title.toLowerCase().includes(query) ||
      post.excerpt.toLowerCase().includes(query) ||
      post.tags.some(tag => tag.toLowerCase().includes(query))
    );
  }, [searchQuery, blogPosts]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Spharaka <span className="text-[#8932c8]">Insights</span></h1>
              <p className="text-xl text-gray-600 mb-10">
                Stay ahead with expert perspectives, product updates, and the latest in cybersecurity innovation.
              </p>
              <div className="relative max-w-2xl mx-auto">
                <Input 
                  type="text" 
                  placeholder="Search articles..." 
                  className="pl-10 pr-4 py-3 w-full rounded-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Achievement Banner */}
        <section className="py-12 bg-gradient-to-r from-purple-50 to-indigo-50 border-y border-purple-100">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Spharaka Named <span className="text-[#8932c8]">Emerging Startup of the Year 2025</span>
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Featured by Business Connect Magazine as one of India's most promising emerging startups, recognizing our innovation in protecting the digital frontier with cutting-edge cybersecurity solutions.
                </p>
                <Button 
                  asChild 
                  className="bg-[#8932c8] hover:bg-[#7a2cb3]"
                >
                  <a 
                    href="https://businessconnectindia.in/spharaka-digital-security-innovation/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Read Full Article →
                  </a>
                </Button>
              </div>
              <div className="order-1 lg:order-2">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/images/emerging-startup-2025.webp" 
                    alt="Spharaka - Emerging Startup of the Year 2025 by Business Connect Magazine"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Indian Business Excellence Awards 2025 Banner */}
        <section className="py-12 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Indian Business Excellence Awards 2025 & <span className="text-[#8932c8]">Global Titan Company of the Year</span>
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Spharaka recognized at the prestigious Indian Business Excellence Awards 2025 for outstanding contributions to digital security and innovation in the cybersecurity industry.
                </p>
                <Button 
                  asChild 
                  className="bg-[#8932c8] hover:bg-[#7a2cb3]"
                >
                  <a 
                    href="https://www.aninews.in/news/business/presenting-the-indian-business-excellence-awards-2025-amp-global-titan-company-of-the-year-202520251127145458/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Read Full Article →
                  </a>
                </Button>
              </div>
              <div className="flex justify-center items-center">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/images/corporate-connect-award-2025.png" 
                    alt="Spharaka - Visionary Startup in AI & Cybersecurity 2025 - Corporate Connect Award"
                    className="w-full h-auto max-w-[200px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex space-x-4 mb-4">
                  {filteredPosts[0].tags.map((tag, i) => (
                    <span key={i} className="text-xs font-medium bg-[#8932c8]/10 text-[#8932c8] px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {filteredPosts[0].title}
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  {filteredPosts[0].excerpt}
                </p>
                <div className="flex items-center text-sm text-gray-500 mb-6">
                  <User className="h-4 w-4 mr-1" />
                  <span className="mr-4">{filteredPosts[0].author}</span>
                  <Calendar className="h-4 w-4 mr-1" />
                  <span className="mr-4">{filteredPosts[0].date}</span>
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{filteredPosts[0].readTime}</span>
                </div>
                <Button asChild className="bg-[#8932c8] hover:bg-[#7a2cb3]">
                  <Link to={`/blogs/${filteredPosts[0].slug}`}>Read Full Article</Link>
                </Button>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={filteredPosts[0].image} 
                  alt={filteredPosts[0].title} 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Recent Posts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">
              {searchQuery ? `Search Results (${filteredPosts.length})` : "Recent Articles"}
            </h2>
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600 mb-4">No articles found matching "{searchQuery}"</p>
                <Button 
                  variant="outline" 
                  onClick={() => setSearchQuery("")}
                  className="border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8]/5"
                >
                  Clear Search
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(searchQuery ? filteredPosts : filteredPosts.slice(1)).map((post) => (
                <div key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex space-x-2 mb-3">
                      {post.tags.slice(0, 2).map((tag, i) => (
                        <span key={i} className="text-xs font-medium bg-[#8932c8]/10 text-[#8932c8] px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-bold text-xl mb-2">{post.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center text-xs text-gray-500 mb-4">
                      <Calendar className="h-3 w-3 mr-1" />
                      <span className="mr-3">{post.date}</span>
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{post.readTime}</span>
                    </div>
                    <Button variant="ghost" asChild className="text-[#8932c8] hover:text-[#7a2cb3] hover:bg-[#8932c8]/5 p-0">
                      <Link to={`/blogs/${post.slug}`}>Read More →</Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {!searchQuery && (
              <div className="text-center mt-12">
                <Button variant="outline" className="border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8]/5">
                  View All Articles
                </Button>
              </div>
            )}
            </>
            )}
          </div>
        </section>

        {/* Subscribe */}
        <section className="py-16 bg-[#8932c8]">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
              <p className="text-lg mb-8 text-white/90">
                Subscribe to our newsletter to receive the latest insights on cybersecurity, AI, and threat detection.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Input 
                  type="email" 
                  placeholder="Your email address" 
                  className="bg-white text-gray-900 placeholder:text-gray-500"
                />
                <Button className="bg-white text-[#8932c8] hover:bg-gray-100">
                  Subscribe
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Blogs;